import React, { useState } from 'react';
import { useTeacher } from '../../../../context/TeacherContext';
import apiAxiosInstance from '../../../../utils/axiosInstance';
import { getAiBackendUrl } from '../../../../constants/api';
import { buildAiAuthHeaders, consumeSSEStream } from '../../../../hooks/useSSEStream';
import {
  exportElementToPdf,
  downloadWordHtmlBlob,
  printHtmlDocument,
  sanitizeFilename,
} from '../../../../utils/exportUtils';
import { createPrintContent, markdownStyles } from '../assessmentsFormatUtils';
import {
  type Question,
  type PassageData,
  type AssessmentResponse,
} from '../assessmentsTypes';
import { useAssessmentForm } from './useAssessmentForm';

export function useAssessmentsPage() {
  const { teacher } = useTeacher();
  
  const [error, setError] = useState<string | null>(null);
  const form = useAssessmentForm({ teacher, setError });
  const {
    topic, setTopic, genre, setGenre, questionStyle, setQuestionStyle, showAnswerKey,
    isPairedPassage, setIsPairedPassage, genreTwo, setGenreTwo,
    revisingAndEditing, setRevisingAndEditing, language, setLanguage,
    gradeLevel, standards, selectedStandards, setSelectedStandards,
    availableGradeLevels, loadingGradeLevels, selectedStrand, setSelectedStrand,
    isFormCollapsed, setIsFormCollapsed, paragraphCount, setParagraphCount,
    questionMix, setQuestionMix, generateQuestions, setGenerateQuestions,
    getDefaultParagraphCount, getActualTextStyle, availableStrands, strandFilteredStandards,
    standardLoading, capitalizeFirstLetter, handleGradeLevelChange,
    generateImages, setGenerateImages, imageCount, setImageCount,
  } = form;

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const [streamedContent2, setStreamedContent2] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [answerKeyBuffer, setAnswerKeyBuffer] = useState<string>('');
  const [isCollectingAnswerKey, setIsCollectingAnswerKey] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [savedAssessmentId, setSavedAssessmentId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [modificationStreamingQuestions, setModificationStreamingQuestions] = useState<{[passageNumber: number]: Question[]}>({});
  const [isPassageModificationActive, setIsPassageModificationActive] = useState<{[passageNumber: number]: boolean}>({});
  const [generationStreamingQuestions, setGenerationStreamingQuestions] = useState<{[passageNumber: number]: string}>({});
  const [generationStreamingCombinedQuestions, setGenerationStreamingCombinedQuestions] = useState<string>('');

  const [assessmentResponse, setAssessmentResponse] = useState<AssessmentResponse | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('');

  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{
    id: string;
    message: string;
    timestamp: Date;
    type: 'user' | 'system' | 'assistant';
  }>>([]);
  const [isChatProcessing, setIsChatProcessing] = useState(false);
  const [chatAbortController, setChatAbortController] = useState<AbortController | null>(null);

  const [streamingPassageContent, setStreamingPassageContent] = useState<{[key: number]: string}>({});
  const [streamingQuestionsContent, setStreamingQuestionsContent] = useState<string>('');
  const [streamingAnswerKeyContent, setStreamingAnswerKeyContent] = useState<string>('');
  const [streamingPassages, setStreamingPassages] = useState<Array<{
    number: number;
    title: string;
    content: string;
    genre?: string;
    isComplete: boolean;
  }>>([]);
  const [isQuestionsComplete, setIsQuestionsComplete] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  /**
   * Strip illustrations from passages before sending to question/answer-key APIs.
   * Base64 images are too large for request payloads and aren't needed for question generation.
   */
  const stripIllustrationsFromPassages = (passages: PassageData[]): PassageData[] => {
    return passages.map(passage => ({
      ...passage,
      illustrations: undefined // Remove illustrations to reduce payload size
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation: topic optional; genres required
    if (isPairedPassage) {
      if (!genre.trim()) {
        setError('Genre for Passage 1 is required');
        return;
      }
      if (!genreTwo.trim()) {
        setError('Genre for Passage 2 is required');
        return;
      }
    } else {
      if (!genre.trim()) {
        setError('Genre is required');
        return;
      }
    }
    
    setIsStreaming(true);
    setError(null);
    setIsLoading(true);
    setAssessmentResponse(null);
    setHasUnsavedChanges(false);
    setCurrentStatus(''); // No status message - streaming shows progress
    setIsFormCollapsed(true); // Collapse the form when generating

    // Clear any previous streaming content
    setStreamingPassageContent({});
    setStreamingQuestionsContent('');
    setStreamingAnswerKeyContent('');
    setStreamingPassages([]); // Clear streaming passages
    setIsQuestionsComplete(false);
    
    // Create abort controller
    const controller = new AbortController();
    setAbortController(controller);

    // Initialize assessment data
    let finalPassages: PassageData[] = [];
    let finalQuestions: Question[] = [];
    let finalAnswerKey: string | undefined = undefined;
    
    // Initialize streaming passages for display
    const numPassages = isPairedPassage ? 2 : 1;
    const initialStreamingPassages = Array.from({ length: numPassages }, (_, i) => ({
      number: i + 1,
      title: `Passage ${i + 1}`,
      content: '',
      genre: i === 0 ? genre : genreTwo,
      isComplete: false
    }));
    setStreamingPassages(initialStreamingPassages);

    try {
        const AI_URL = getAiBackendUrl();
        
        // Prepare standards
        const preparedStandards = selectedStandards
          .map((id) => {
            const standard = standards.find((s) => s._id === id);
            if (standard) {
              return {
                id: standard._id,
                code: String(standard.code || standard.standard || ''),
                standard: String(standard.standard || ''),
                description: String(standard.description || '')
              };
            }
            return null;
          })
          .filter(Boolean);

        // STEP 1: Generate Passages with Streaming
        setCurrentStatus('Generating passage(s)...');
        
        const passageRequest = {
          isPairedPassage,
          topic,
          genre,
          gradeLevel,
          paragraphCount,
          standards: preparedStandards,
          passages: isPairedPassage ? [{ topic, genre }, { topic, genre: genreTwo }] : undefined,
          revisingAndEditing,
          language,
          textStyle: getActualTextStyle(questionStyle),
          generateImages,
          imageCount: generateImages ? imageCount : 1,
        };
        
        await consumeSSEStream({
          url: `${AI_URL}/generate-assessment-passage`,
          headers: buildAiAuthHeaders(),
          body: JSON.stringify(passageRequest),
          signal: controller.signal,
          onEvent: (data) => {
            switch (data.type) {
              case 'passage_content': {
                const passageNumber = data.passageNumber as number;
                const chunk = String(data.content ?? '');
                setStreamingPassageContent(prev => ({
                  ...prev,
                  [passageNumber]: (prev[passageNumber] || '') + chunk,
                }));
                setStreamingPassages(prev => prev.map(p =>
                  p.number === passageNumber ? { ...p, content: p.content + chunk } : p,
                ));
                break;
              }
              case 'passage_complete': {
                const passageNumber = data.passageNumber as number;
                const passage = data.passage as { title?: string };
                setStreamingPassages(prev => prev.map(p =>
                  p.number === passageNumber
                    ? { ...p, title: passage?.title || p.title, isComplete: true }
                    : p,
                ));
                break;
              }
              case 'image_generating': {
                // Optional: Could show progress indicator
                const passageNumber = data.passageNumber as number;
                const placementIndex = data.placementIndex as number;
                console.log(`Generating image for passage ${passageNumber} at paragraph ${placementIndex}`);
                break;
              }
              case 'images_complete': {
                const passageNumber = data.passageNumber as number;
                const illustrations = data.illustrations as any[];
                console.log(`Images complete for passage ${passageNumber}:`, illustrations);
                // Update the final passage with illustrations
                if (finalPassages[passageNumber - 1]) {
                  finalPassages[passageNumber - 1].illustrations = illustrations;
                }
                break;
              }
              case 'complete': {
                const payload = data.data as { passages?: typeof finalPassages };
                if (payload?.passages) {
                  // Preserve any illustrations that were already added via images_complete events
                  const preservedPassages = payload.passages.map((passage, idx) => {
                    const existing = finalPassages[idx];
                    if (existing?.illustrations && existing.illustrations.length > 0) {
                      // Keep the illustrations from the images_complete event
                      return { ...passage, illustrations: existing.illustrations };
                    }
                    return passage;
                  });
                  finalPassages = preservedPassages;
                }
                console.log('Generation complete, final passages:', finalPassages.map(p => ({
                  title: p.title,
                  hasIllustrations: !!p.illustrations,
                  illustrationCount: p.illustrations?.length || 0
                })));
                break;
              }
              case 'error':
                throw new Error(String(data.error));
            }
          },
        });

        // STEP 2: Generate Questions if requested
        if (generateQuestions && finalPassages.length > 0) {
          setStreamingQuestionsContent('');
          setIsQuestionsComplete(false);

          // Strip illustrations to reduce payload size (images not needed for question generation)
          const passagesForQuestions = stripIllustrationsFromPassages(finalPassages);

          const questionsRequest = {
            passages: passagesForQuestions,
            isPairedPassage,
            questionMix,
            generateCombinedQuestions: isPairedPassage,
            standards: preparedStandards,
            gradeLevel,
            revisingAndEditing,
            language,
            textStyle: getActualTextStyle(questionStyle)
          };
          
          await consumeSSEStream({
            url: `${AI_URL}/generate-assessment-questions`,
            headers: buildAiAuthHeaders(),
            body: JSON.stringify(questionsRequest),
            signal: controller.signal,
            onEvent: (data) => {
              switch (data.type) {
                case 'combined_questions_content':
                  setStreamingQuestionsContent(prev => prev + String(data.content ?? ''));
                  break;
                case 'complete': {
                  const payload = data.data as {
                    passages?: typeof finalPassages;
                    combinedQuestions?: Question[];
                  };
                  if (payload?.passages) {
                    // Preserve illustrations from existing passages when updating from question generation
                    finalPassages = payload.passages.map((passage, idx) => {
                      const existing = finalPassages[idx];
                      if (existing?.illustrations && existing.illustrations.length > 0) {
                        // Keep the illustrations from the original passage
                        return { ...passage, illustrations: existing.illustrations };
                      }
                      return passage;
                    });
                  }
                  finalQuestions = payload?.combinedQuestions || [];
                  setIsQuestionsComplete(true);
                  break;
                }
                case 'error':
                  throw new Error(String(data.error));
              }
            },
          });
        }

        // STEP 3: Generate Answer Key if requested (with streaming)
        // Only generate answer key if questions exist or were generated
        const hasQuestions = finalPassages.some(p => p.questions && p.questions.length > 0);
        if (showAnswerKey && finalPassages.length > 0 && (generateQuestions || hasQuestions)) {
          // Strip illustrations to reduce payload size (images not needed for answer key generation)
          const passagesForAnswerKey = stripIllustrationsFromPassages(finalPassages);

          const answerKeyRequest = {
            passages: passagesForAnswerKey,
            combinedQuestions: finalQuestions,
            standards: preparedStandards,
            isPairedPassage,
            language
          };
          
          await consumeSSEStream({
            url: `${AI_URL}/generate-assessment-answer-key`,
            headers: buildAiAuthHeaders(),
            body: JSON.stringify(answerKeyRequest),
            signal: controller.signal,
            onEvent: (data) => {
              switch (data.type) {
                case 'answer_key_content':
                  setStreamingAnswerKeyContent(prev => prev + String(data.content ?? ''));
                  break;
                case 'complete': {
                  const payload = data.data as { answerKey?: string };
                  if (payload?.answerKey) finalAnswerKey = payload.answerKey;
                  break;
                }
                case 'error':
                  throw new Error(String(data.error));
              }
            },
          });
        }

        // Set the final assessment response
        const finalAssessmentResponse: AssessmentResponse = {
          passages: finalPassages,
          combinedQuestions: finalQuestions,
          answerKey: finalAnswerKey,
          isPairedPassage
        };
        
        setAssessmentResponse(finalAssessmentResponse);
        
        // Auto-save
        await handleAutoSave(finalAssessmentResponse);

    } catch (error: any) {
        if (error.name === 'AbortError') {
          setError('Generation cancelled');
        } else {
          console.error('Assessment generation error:', error);
          
          let errorMessage = 'An unexpected error occurred during assessment generation';
          
          if (error.name === 'TimeoutError') {
            errorMessage = 'The assessment generation is taking longer than expected. Please try again.';
          } else if (error.message?.includes('timeout')) {
            errorMessage = 'The AI service timed out. Please try again.';
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          setError(errorMessage);
        }
    } finally {
        setIsStreaming(false);
        setIsLoading(false);
        setAbortController(null);
        setStreamingPassageContent({});
        setStreamingQuestionsContent('');
        setStreamingAnswerKeyContent('');
        // Clear the status indicator so the circular progress disappears
        setCurrentStatus('');
    }
};

  // Chat handlers for modification
  const handleChatSubmit = async () => {
    if (!chatMessage.trim() || !assessmentResponse) return;

    const userMessage = {
      id: Date.now().toString(),
      message: chatMessage.trim(),
      timestamp: new Date(),
      type: 'user' as const
    };

    setChatHistory(prev => [...prev, userMessage]);
    setChatMessage('');
    setIsChatProcessing(true);

    const controller = new AbortController();
    setChatAbortController(controller);

    try {
        const AI_URL = getAiBackendUrl();

      // Prepare modification request
      // Extract all questions from passages if combinedQuestions is empty
      const extractQuestionsFromPassages = (passages: PassageData[]): Question[] => {
        const questions: Question[] = [];
        passages.forEach(passage => {
          if (passage.questions && passage.questions.length > 0) {
            questions.push(...passage.questions);
          }
        });
        return questions;
      };
      
      const allQuestions = (assessmentResponse.combinedQuestions && assessmentResponse.combinedQuestions.length > 0)
        ? assessmentResponse.combinedQuestions
        : extractQuestionsFromPassages(assessmentResponse.passages);
      
      // Prepare standards - ensure they're in correct format
      const preparedStandards = selectedStandards
        .map(id => {
          const standard = standards.find(s => s._id === id);
          if (!standard) return null;
          return {
            code: String(standard.code || ''),
            description: String(standard.description || '')
          };
        })
        .filter((s): s is { code: string; description: string } => s !== null);

      const modificationRequest = {
        userRequest: userMessage.message,
        currentPassages: assessmentResponse.passages,
        currentQuestions: allQuestions,
        assessmentForm: {
          isPairedPassage,
          gradeLevel,
          standards: preparedStandards,
          paragraphCount,
          questionStyle: getActualTextStyle(questionStyle),
          language
        }
      };

      let modifiedPassages = [...assessmentResponse.passages];
      let modifiedQuestions = [...(assessmentResponse.combinedQuestions || [])];

      // REPLACE streaming content (not append) - this is key difference from chat
      setStreamingPassageContent({});
      setStreamingQuestionsContent('');
      setStreamingAnswerKeyContent('');
      setIsQuestionsComplete(false);
      
      // Initialize streaming passages for modification
      // If modifying questions only, show existing passage content
      const numPassages = assessmentResponse.passages.length;
      const freshStreamingPassages = Array.from({ length: numPassages }, (_, i) => ({
        number: i + 1,
        title: assessmentResponse.passages[i]?.title || `Passage ${i + 1}`,
        // Show existing passage content (will be replaced if passage is being modified)
        content: assessmentResponse.passages[i]?.passage || '',
        genre: assessmentResponse.passages[i]?.genre,
        isComplete: true // Mark as complete initially (will be set to false if passage is being modified)
      }));
      setStreamingPassages(freshStreamingPassages);

      await consumeSSEStream({
        url: `${AI_URL}/modify-assessment-content`,
        headers: buildAiAuthHeaders(),
        body: JSON.stringify(modificationRequest),
        signal: controller.signal,
        onEvent: (data) => {
              switch (data.type) {
                case 'status':
                  // Status updates - silent during streaming
                  break;

                case 'passage_content': {
                  const passageNumber = data.passageNumber as number;
                  const chunk = String(data.content ?? '');
                  setStreamingPassageContent(prev => ({
                    ...prev,
                    [passageNumber]: (prev[passageNumber] || '') + chunk,
                  }));

                  setStreamingPassages(prev => prev.map(p => {
                    if (p.number === passageNumber) {
                      return {
                        ...p,
                        content: p.isComplete ? chunk : p.content + chunk,
                        isComplete: false,
                      };
                    }
                    return p;
                  }));
                  break;
                }

                case 'questions_content':
                case 'combined_questions_content':
                  setStreamingQuestionsContent(prev => prev + String(data.content ?? ''));
                  break;

                case 'complete': {
                  const payload = data.data as {
                    passages?: typeof modifiedPassages;
                    questions?: Question[];
                  } | undefined;

                  setStreamingPassageContent({});
                  setStreamingQuestionsContent('');
                  setStreamingAnswerKeyContent('');

                  modifiedPassages = payload?.passages || modifiedPassages;

                  const isPaired = assessmentResponse?.isPairedPassage || false;
                  let finalCombinedQuestions: Question[] = [];

                  if (isPaired && payload?.questions && payload.questions.length > 0) {
                    finalCombinedQuestions = payload.questions;
                  }

                  const updatedAssessment: AssessmentResponse = {
                    ...assessmentResponse,
                    passages: modifiedPassages,
                    combinedQuestions: finalCombinedQuestions,
                  };

                  setAssessmentResponse(updatedAssessment);
                  setHasUnsavedChanges(true);
                  setIsChatProcessing(false);
                  break;
                }

                case 'error':
                  throw new Error(String(data.error));
              }
        },
        onParseError: (parseError) => {
          console.error('Error parsing SSE data:', parseError);
        },
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setChatHistory(prev => [...prev, {
          id: Date.now().toString(),
          message: '⛔ Modification cancelled',
          timestamp: new Date(),
          type: 'system' as const
        }]);
      } else {
        console.error('Modification error:', error);
        setChatHistory(prev => [...prev, {
          id: Date.now().toString(),
          message: `❌ Error: ${error.message}`,
          timestamp: new Date(),
          type: 'system' as const
        }]);
      }
    } finally {
      setIsChatProcessing(false);
      setChatAbortController(null);
      setStreamingPassageContent({});
      setStreamingQuestionsContent('');
      setStreamingAnswerKeyContent('');
      // Clear the status indicator so the circular progress disappears
      setCurrentStatus('');
    }
  };

  const handleChatCancel = () => {
    if (chatAbortController) {
      chatAbortController.abort();
      setChatAbortController(null);
    }
  };

  const handleAutoSave = async (assessmentData?: AssessmentResponse) => {
    console.log('🟡 handleAutoSave called');
    console.log('🟡 assessmentData param:', assessmentData);
    console.log('🟡 assessmentData._id:', assessmentData?._id);
    console.log('🟡 assessmentData passages preview:', assessmentData?.passages?.[0]?.passage?.substring(0, 100));
    
    if (!teacher?._id) {
      console.log('❌ No teacher ID available for saving');
      setError('Unable to save: No teacher information available');
      return;
    }

    // Use passed assessmentData if available, otherwise fall back to state variables
    const dataToUse = assessmentData || assessmentResponse;
    console.log('🟡 dataToUse._id:', dataToUse?._id);
    console.log('🟡 dataToUse passages preview:', dataToUse?.passages?.[0]?.passage?.substring(0, 100));
    const hasAssessmentData = dataToUse && dataToUse.passages && dataToUse.passages.length > 0;
    const hasStreamedData = streamedContent && streamedContent.trim().length > 0;
    
    if (!hasAssessmentData && !hasStreamedData) {
      console.log('❌ No assessment data to save');
      console.log('❌ assessmentData:', assessmentData);
      console.log('❌ assessmentResponse:', assessmentResponse);
      console.log('❌ streamedContent length:', streamedContent?.length);
      return;
    }

    try {
      console.log('✅ Starting auto-save');
      console.log('✅ Teacher ID:', teacher._id);
      console.log('✅ Has assessment response data:', hasAssessmentData);
      console.log('✅ Has streamed data:', hasStreamedData);
      setIsSaving(true);

      // Use assessmentResponse data if available, otherwise fall back to original variables
      let saveData;
      
      if (hasAssessmentData) {
        // Use new format
        const firstPassage = dataToUse.passages[0];
        const allQuestions = dataToUse.passages.flatMap(p => p.questions || []);
        
        if (dataToUse.combinedQuestions) {
          allQuestions.push(...dataToUse.combinedQuestions);
        }

        // Always use the new passages array format for consistency
        saveData = {
          title: firstPassage?.title || topic || 'Untitled Assessment',
          passages: dataToUse.passages.map(p => ({
            title: p.title || topic || 'Untitled',
            passage: p.passage || '',
            genre: p.genre || genre || 'Informational',
            gradeLevel: p.gradeLevel || gradeLevel,
            questions: (p.questions || []).map((q) => ({
              question: q.question || '',
              answers: q.answers || [],
              correctAnswer: q.correctAnswer || '',
              explanation: q.explanation || '',
              standardReference: q.standardReference || '',
              bloomsLevel: q.bloomsLevel || '',
              answerType: q.answerType || 'multiple_choice'
            }))
          })),
          genre: firstPassage?.genre || genre || 'Informational',
          gradeLevel: firstPassage?.gradeLevel || gradeLevel,
          answerKey: dataToUse.answerKey || '',
          combinedQuestions: (dataToUse.combinedQuestions || []).map((q) => ({
            question: q.question || '',
            answers: q.answers || [],
            correctAnswer: q.correctAnswer || '',
            explanation: q.explanation || '',
            standardReference: q.standardReference || '',
            bloomsLevel: q.bloomsLevel || '',
            answerType: q.answerType || 'multiple_choice'
          })),
          isPairedPassage: dataToUse.isPairedPassage || false,
          teacherId: teacher._id,
          isAIGenerated: true,
          includeAnswerKey: true
        };
      } else {
        // Use original streaming variables - convert to new passages array format
        const mainPassage = {
          title: topic || 'Untitled Assessment',
          passage: streamedContent || '',
          genre: genre || 'Informational',
          gradeLevel: gradeLevel,
          questions: questions.map((q) => ({
            question: q.question || '',
            answers: q.answers || [],
            correctAnswer: q.correctAnswer || '',
            explanation: q.explanation || '',
            standardReference: q.standardReference || '',
            bloomsLevel: q.bloomsLevel || '',
            answerType: q.answerType || 'multiple_choice'
          }))
        };

        saveData = {
          title: topic || 'Untitled Assessment',
          passages: isPairedPassage
            ? [
                mainPassage,
                {
                  title: topic || 'Untitled Assessment',
                  passage: streamedContent || '',
                  genre: genreTwo || 'Informational', 
                  gradeLevel: gradeLevel,
                  questions: []
                }
              ]
            : [mainPassage],
          genre: genre || 'Informational',
          gradeLevel,
          answerKey: answerKeyBuffer || '',
          combinedQuestions: [],
          isPairedPassage: isPairedPassage || false,
          teacherId: teacher._id,
          isAIGenerated: true,
          includeAnswerKey: showAnswerKey
        };
      }

      // Add standards to the saveData
      const savePayload = {
        ...saveData,
        standards: selectedStandards.map(id => {
          const standard = standards.find(s => s._id === id);
          return standard ? {
            id: standard._id,
            code: standard.code || standard.standard,
            standard: standard.standard,
            description: standard.description
          } : null;
        }).filter(Boolean),
        standardCodes: selectedStandards
          .map(id => standards.find(s => s._id === id)?.code)
          .filter((code): code is string => !!code)
      };

      // Optimize payload size for Vercel limits
      const optimizeAnswerKey = (answerKey: string): string => {
        if (!answerKey || answerKey.length < 1000) {
          return answerKey;
        }

        // Extract only essential information to reduce size
        const lines = answerKey.split('\n');
        const essentialInfo: string[] = [];
        let currentQuestion = '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          
          // Look for question numbers (standalone numbers)
          if (/^\d+$/.test(trimmedLine)) {
            currentQuestion = trimmedLine;
          }
          
          // Look for correct answers
          if (trimmedLine.startsWith('Correct Answer:')) {
            if (currentQuestion) {
              essentialInfo.push(`Q${currentQuestion}: ${trimmedLine.replace('Correct Answer:', '').trim()}`);
              currentQuestion = '';
            }
          }
        }
        
        // If we found essential info, use it; otherwise truncate
        if (essentialInfo.length > 0) {
          return 'Answer Key:\n' + essentialInfo.join('\n');
        }
        
        // If still too large, truncate more aggressively
        if (answerKey.length > 10000) {
          return answerKey.substring(0, 10000) + '\n\n[Answer key truncated due to size limits]';
        }
        
        return answerKey;
      };

      const originalAnswerKeySize = savePayload.answerKey?.length || 0;
      const optimizedAnswerKey = optimizeAnswerKey(savePayload.answerKey || '');
      const optimizedAnswerKeySize = optimizedAnswerKey.length;

      const optimizedPayload = {
        ...savePayload,
        answerKey: optimizedAnswerKey
      };

      // Check payload size
      const payloadSize = JSON.stringify(optimizedPayload).length;
      const payloadSizeMB = payloadSize / (1024 * 1024);
      
      console.log('Answer key optimization:', {
        original: `${(originalAnswerKeySize / 1024).toFixed(2)} KB`,
        optimized: `${(optimizedAnswerKeySize / 1024).toFixed(2)} KB`,
        reduction: originalAnswerKeySize > 0 ? `${(((originalAnswerKeySize - optimizedAnswerKeySize) / originalAnswerKeySize) * 100).toFixed(1)}%` : '0%'
      });
      console.log('Total payload size:', `${payloadSizeMB.toFixed(2)} MB`);
      
      // Check if payload is still too large
      if (payloadSizeMB > 4.0) { // 4MB limit with buffer
        console.warn('Payload too large, applying additional optimizations');
        
        // Further reduce answer key if still too large
        if (optimizedAnswerKeySize > 50000) { // 50KB limit for answer key
          const furtherOptimizedAnswerKey = optimizedAnswerKey.substring(0, 50000) + '\n\n[Answer key further truncated due to payload size limits]';
          optimizedPayload.answerKey = furtherOptimizedAnswerKey;
          
          const finalPayloadSize = JSON.stringify(optimizedPayload).length;
          const finalPayloadSizeMB = finalPayloadSize / (1024 * 1024);
          console.log('Final payload size after additional optimization:', `${finalPayloadSizeMB.toFixed(2)} MB`);
          
          if (finalPayloadSizeMB > 4.0) {
            throw new Error(`Payload size (${finalPayloadSizeMB.toFixed(2)} MB) exceeds the 4MB limit. Please reduce the answer key length or split the assessment.`);
          }
        }
      }

      // Check if we have an existing ID to update
      const existingId = dataToUse?._id || savedAssessmentId;
      let saveResponse;
      
      console.log('🟢 existingId:', existingId);
      console.log('🟢 optimizedPayload passages[0].passage preview:', optimizedPayload.passages?.[0]?.passage?.substring(0, 200));
      console.log('🟢 optimizedPayload passages[0].questions count:', optimizedPayload.passages?.[0]?.questions?.length);
      console.log('🟢 optimizedPayload combinedQuestions count:', optimizedPayload.combinedQuestions?.length);
      
      if (existingId) {
        // Update existing assessment
        console.log('🔄 Updating existing assessment:', existingId);
        saveResponse = await apiAxiosInstance.put(`/api/assessment-passages/${existingId}`, optimizedPayload);
      } else {
        // Create new assessment
        console.log('➕ Creating new assessment');
        saveResponse = await apiAxiosInstance.post('/api/assessment-passages', optimizedPayload);
      }

      if (saveResponse.status === 201 || saveResponse.status === 200) {
        const assessmentId = saveResponse.data.id || existingId;
        console.log('✅ SAVE SUCCESS! Assessment ID:', assessmentId);
        setSavedAssessmentId(assessmentId);
        
        // Update assessmentResponse with the ID so future modifications update instead of create
        if (assessmentData) {
          assessmentData._id = assessmentId;
        } else if (assessmentResponse) {
          setAssessmentResponse({
            ...assessmentResponse,
            _id: assessmentId
          });
        }
        
        setSuccessMessage('Assessment saved successfully!');
        setShowSuccessAlert(true);
        setHasUnsavedChanges(false);
      } else {
        console.log('❌ SAVE FAILED! Status:', saveResponse.status);
        throw new Error('Failed to save assessment');
      }
    } catch (error: any) {
      console.error('Error saving assessment:', error);
      
      // Handle specific error types
      if (error.response?.status === 413) {
        setError('The assessment is too large to save. Please reduce the answer key length or split into smaller assessments.');
      } else if (error.response?.status === 500 && error.response?.data?.error === 'Function Invocation Failed') {
        setError('The server encountered an error processing your request. This may be due to the payload size. Please try reducing the answer key length.');
      } else {
        setError(error instanceof Error ? error.message : 'Failed to save assessment');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = async () => {
    console.log('🔴 handleManualSave called');
    console.log('🔴 assessmentResponse:', assessmentResponse);
    console.log('🔴 assessmentResponse._id:', assessmentResponse?._id);
    console.log('🔴 savedAssessmentId:', savedAssessmentId);
    console.log('🔴 passages[0].passage preview (first 200):', assessmentResponse?.passages?.[0]?.passage?.substring(0, 200));
    console.log('🔴 passages[0] FULL passage length:', assessmentResponse?.passages?.[0]?.passage?.length);
    console.log('🔴 passages[0] keys:', Object.keys(assessmentResponse?.passages?.[0] || {}));
    console.log('🔴 FULL passages array:', JSON.stringify(assessmentResponse?.passages?.map(p => ({
      title: p.title,
      passageLength: p.passage?.length,
      questionsCount: p.questions?.length
    }))));
    try {
      await handleAutoSave(assessmentResponse || undefined);
    } catch (error) {
      console.error('Manual save error:', error);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessAlert(false);
  };

  // Assessment modification function with streaming
  const handleModifyAssessment = async (modificationPrompt: string) => {
    if (!assessmentResponse) {
      setError('No assessment available to modify');
      return;
    }

    try {
      setIsLoading(true);
      setIsStreaming(true);
      setError(null);
      setCurrentStatus('Starting modification...');

      // Clear any previous modification streaming content
      setModificationStreamingQuestions({});
      setIsPassageModificationActive({});
      
      // Clear any previous generation streaming content
      setGenerationStreamingQuestions({});
      setGenerationStreamingCombinedQuestions('');

      // IMPORTANT: Clear the UI immediately to show we're starting fresh
      // This prevents the "attaching new generation to old content" issue
      setAssessmentResponse(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          passages: prev.passages.map(passage => ({
            ...passage,
            passage: '' // Clear passage content immediately
          }))
        };
      });

      const modificationData = {
        passages: assessmentResponse.passages?.map(p => ({
          title: p.title,
          passage: p.passage,
          questions: p.questions?.filter(q => q && q.answers && Array.isArray(q.answers)) || [],
          genre: p.genre,
          gradeLevel: p.gradeLevel
        })),
        standards: selectedStandards.map(id => {
          const standard = standards.find(s => s._id === id);
          return {
            id: standard?._id || id,
            code: standard?.code || standard?.standard || '',
            standard: standard?.code || standard?.standard || '',
            description: standard?.description || ''
          };
        }).filter(s => s.code),
        modificationPrompt,
        isPairedPassage: assessmentResponse.isPairedPassage
      };

              const response = await fetch(`${getAiBackendUrl()}/modify-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modificationData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      
      // Initialize modification streaming data
      const streamedData = {
        passages: [...assessmentResponse.passages],
        combinedQuestions: assessmentResponse.combinedQuestions || [],
        answerKey: assessmentResponse.answerKey || null,
        isPairedPassage: assessmentResponse.isPairedPassage
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('Modification streaming completed');
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              // Debug: Log the raw line to see what we're trying to parse
              console.log('Processing line:', line);
              try {
                const data = JSON.parse(line.slice(6));
                console.log('Received modification stream data:', data);
                
                // Additional debugging for content chunks
                if (data.type === 'passage_modification_content' && data.content) {
                  console.log('Content chunk:', data.content);
                }
                
                switch (data.type) {
                  case 'start':
                    setCurrentStatus('Starting modification...');
                    break;
                    
                  case 'status':
                    // Enhanced status handling with paragraph count information (from SmallGroup)
                    if (data.paragraphChange !== undefined) {
                      const changeMessage = data.paragraphChange > 0 ? 
                        `📊 ${data.content} (+${data.paragraphChange} paragraphs)` :
                        data.paragraphChange < 0 ? 
                        `📊 ${data.content} (${data.paragraphChange} paragraphs)` :
                        `📊 ${data.content}`;
                      setCurrentStatus(changeMessage);
                    } else {
                      setCurrentStatus(data.content);
                    }
                    break;
                    
                  case 'passage_modification_content':
                    const passageIndex = data.passageNumber - 1;
                    
                    // Initialize passage if not exists
                    if (!streamedData.passages[passageIndex]) {
                      streamedData.passages[passageIndex] = {
                        title: '',
                        passage: '',
                        questions: [],
                        genre: '',
                        gradeLevel: ''
                      };
                    }
                    
                    // For modification, accumulate content directly like initial generation
                    // This prevents the "one paragraph appears, then disappears" issue
                    streamedData.passages[passageIndex].passage += data.content;
                    
                    // Update UI with streaming passage content (same as initial generation)
                    setAssessmentResponse(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        passages: prev.passages.map((passage, index) => 
                          index === passageIndex 
                            ? { ...passage, passage: streamedData.passages[passageIndex].passage }
                            : passage
                        ),
                        combinedQuestions: streamedData.combinedQuestions,
                        answerKey: streamedData.answerKey || undefined,
                        isPairedPassage: streamedData.isPairedPassage
                      };
                    });
                    
                    setCurrentStatus(`Modifying passage ${data.passageNumber}... (streaming)`);
                    break;
                    
                  case 'passage_modification_complete':
                    const pIndex = data.passageNumber - 1;
                    
                    // Parse the JSON content and extract the formatted passage
                    let formattedContent = data.content;
                    try {
                      // Try to parse as JSON first (in case it's still in JSON format)
                      const parsed = JSON.parse(data.content);
                      if (parsed.content) {
                        formattedContent = parsed.content;
                      } else if (parsed.passage) {
                        formattedContent = parsed.passage;
                      }
                    } catch (e) {
                      // If not JSON, use the content as-is
                      formattedContent = data.content;
                    }
                    
                    // Update the passage with the final formatted content
                    streamedData.passages[pIndex].passage = formattedContent;
                    
                    // Update the displayed assessment immediately
                    setAssessmentResponse(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        passages: prev.passages.map((passage, index) => 
                          index === pIndex 
                            ? { ...passage, passage: formattedContent }
                            : passage
                        ),
                        combinedQuestions: streamedData.combinedQuestions,
                        answerKey: streamedData.answerKey || undefined,
                        isPairedPassage: streamedData.isPairedPassage
                      };
                    });
                    
                    // Enhanced completion message with paragraph count information (from SmallGroup)
                    if (data.paragraphChange !== undefined) {
                      const changeMessage = data.paragraphChange > 0 ? 
                        `✅ Passage ${data.passageNumber} modified! (+${data.paragraphChange} paragraphs: ${data.previousParagraphCount} → ${data.paragraphCount})` :
                        data.paragraphChange < 0 ? 
                        `✅ Passage ${data.passageNumber} modified! (${data.paragraphChange} paragraphs: ${data.previousParagraphCount} → ${data.paragraphCount})` :
                        `✅ Passage ${data.passageNumber} modified! (${data.paragraphCount} paragraphs maintained)`;
                      setCurrentStatus(changeMessage);
                    } else {
                      setCurrentStatus(`Passage ${data.passageNumber} modification completed!`);
                    }
                    break;
                    
                  case 'questions_modification_content':
                    // Accumulate streaming questions content (same as initial generation)
                    setGenerationStreamingQuestions(prev => ({
                      ...prev,
                      [data.passageNumber]: (prev[data.passageNumber] || '') + data.content
                    }));
                    setCurrentStatus(`Modifying questions for Passage ${data.passageNumber}... (streaming)`);
                    break;
                    
                  case 'questions_modification_complete':
                    const qIndex = data.passageNumber - 1;
                    streamedData.passages[qIndex].questions = data.questions;
                    
                    // Store the completed questions for this passage
                    setModificationStreamingQuestions(prev => ({
                      ...prev,
                      [data.passageNumber]: data.questions
                    }));
                    
                    // Clear the streaming content for this passage since it's complete
                    // No need to clear since we're using the same approach as initial generation
                    
                    // Update the displayed assessment immediately (preserve existing metadata like _id)
                    setAssessmentResponse(prev => ({
                      ...prev,
                      passages: [...streamedData.passages],
                      combinedQuestions: streamedData.combinedQuestions,
                      answerKey: streamedData.answerKey || undefined,
                      isPairedPassage: streamedData.isPairedPassage
                    }));
                    setCurrentStatus(`Questions for Passage ${data.passageNumber} modified!`);
                    break;
                    
                  case 'combined_questions_content':
                    // Accumulate streaming combined questions content (same as initial generation)
                    setGenerationStreamingCombinedQuestions(prev => prev + data.content);
                    setCurrentStatus('Modifying combined questions... (streaming)');
                    break;
                    
                  case 'complete':
                    console.log('🟣 COMPLETE event (handleModifyAssessment)');
                    console.log('🟣 data.data:', data.data);
                    console.log('🟣 data.data.passages:', data.data.passages);
                    console.log('🟣 data.data.passages[0].passage preview:', data.data.passages?.[0]?.passage?.substring(0, 200));
                    setAssessmentResponse(prev => {
                      console.log('🟣 prev assessmentResponse passages preview:', prev?.passages?.[0]?.passage?.substring(0, 200));
                      const merged = {
                        ...prev,
                        ...data.data
                      };
                      console.log('🟣 merged passages preview:', merged.passages?.[0]?.passage?.substring(0, 200));
                      return merged;
                    });
                    setHasUnsavedChanges(true);
                    setCurrentStatus('Modification complete!');
                    // User will save manually via the Save Changes button
                    setSuccessMessage('Assessment modified - click Save Changes to save');
                    setShowSuccessAlert(true);
                    break;
                    
                  case 'end':
                    setCurrentStatus('');
                    break;
                    
                  case 'error':
                    console.error('Modification stream error:', data.content);
                    
                    // Check if this is a false error (content that got flagged as error)
                    if (data.content && typeof data.content === 'string' && data.content.includes('expedition')) {
                      console.log('Detected potential false error with "expedition". This might be content, not an error.');
                      // Don't treat this as an error, continue processing
                      break;
                    }
                    
                    setError(data.content);
                    return;
                    
                  default:
                    console.log('Unknown modification stream data type:', data.type);
                }
              } catch (parseError) {
                console.error('Error parsing modification stream data:', parseError, 'Line:', line, 'Raw line content:', line);
                
                // Try to extract any useful information from the line
                if (line.includes('expedition')) {
                  console.log('Found "expedition" in raw line, this might be content, not an error');
                }
                
                // Check if this is a content line that got malformed
                if (line.startsWith('data: ') && line.length > 6) {
                  const rawContent = line.slice(6);
                  console.log('Raw content that failed to parse:', rawContent);
                  
                  // If it looks like content (not JSON), try to handle it as a content chunk
                  if (!rawContent.startsWith('{') && !rawContent.startsWith('[')) {
                    console.log('This appears to be raw content, not JSON. Attempting to handle as content chunk.');
                    // Try to treat this as a content chunk for the current passage
                    const currentPassageIndex = 0; // Default to first passage
                    if (streamedData.passages[currentPassageIndex]) {
                      // Accumulate content directly like initial generation
                      streamedData.passages[currentPassageIndex].passage += rawContent;
                      
                      // Update UI with the content
                      setAssessmentResponse(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          passages: prev.passages.map((passage, index) => 
                            index === currentPassageIndex 
                              ? { ...passage, passage: streamedData.passages[currentPassageIndex].passage }
                              : passage
                          ),
                          combinedQuestions: streamedData.combinedQuestions,
                          answerKey: streamedData.answerKey || undefined,
                          isPairedPassage: streamedData.isPairedPassage
                        };
                      });
                    }
                  }
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

    } catch (error: any) {
      console.error('Modification streaming error:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Check if this is the "expedition" error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('expedition')) {
        console.log('Detected "expedition" in main error. This might be content, not an error.');
        // Don't set this as an error, just log it
        console.log('Continuing with modification process...');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsStreaming(false);
      setIsLoading(false);
      
      // Clear the status indicator so the circular progress disappears
      setCurrentStatus('');
      
      // Clear modification streaming content when done
      setModificationStreamingQuestions({});
      setIsPassageModificationActive({});
      
      // Clear generation streaming content as well
      setGenerationStreamingQuestions({});
      setGenerationStreamingCombinedQuestions('');
    }
  };

  // -- Download menu handlers --
  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  // -- Print handler --
  const handlePrint = () => {
    if (!assessmentResponse) return;

    const content = createPrintContent(
      assessmentResponse.passages,
      assessmentResponse.combinedQuestions,
      assessmentResponse.answerKey,
      assessmentResponse.isPairedPassage,
      'print',
      standards.filter(s => selectedStandards.includes(s._id)).map(s => s.code || s.standard).filter(Boolean) as string[]
    );

    printHtmlDocument(content, {
      title: 'Assessment',
      styles: `
            ${markdownStyles}
            .passage-section { margin-bottom: 3em; }
            .passage-title {
              text-align: center; font-size: 1.5em; font-weight: bold;
              margin-bottom: 1.5em; color: #2c5282;
              border-bottom: 2px solid #2c5282; padding-bottom: 0.5em;
            }
            .passage-content {
              font-size: 1.1em; line-height: 1.8; text-align: justify;
              margin: 0 auto; max-width: 85ch; font-family: 'Times New Roman', serif;
            }
            .passage-content p {
              margin-bottom: 1.2em; text-indent: 2em;
              white-space: pre-line; text-align: justify;
            }
            .next-passage, .questions-section, .answer-key-section { page-break-before: always; }
            .question, .answer-choices, .answer-choice, .answer-key-item {
              page-break-inside: avoid !important;
            }
            @media print {
              @page { margin: 0.5in; }
              h1 { margin-top: 0; }
            }
          `,
    });
  };

  // -- Download as PDF --
  const handleDownloadPDF = async () => {
    if (!assessmentResponse) return;

    const content = createPrintContent(
      assessmentResponse.passages,
      assessmentResponse.combinedQuestions,
      assessmentResponse.answerKey,
      assessmentResponse.isPairedPassage,
      'pdf',
      standards.filter(s => selectedStandards.includes(s._id)).map(s => s.code || s.standard).filter(Boolean) as string[]
    );

    const element = document.createElement('div');
    element.innerHTML = `
      <style>
        body { font-family: 'Times New Roman', serif; font-size: 14px; line-height: 1.6; }
        .passage-title { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 1.5em; color: #2c5282; }
        .passage-content p { margin-bottom: 1.5em; text-indent: 2em; text-align: justify; }
        .questions-section { page-break-inside: avoid; }
        .questions-section h2 { 
          font-size: 16px; 
          margin: 2em 0 1.2em 0; 
          text-align: center; 
          color: #2c5282; 
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .question-text { 
          margin-bottom: 1em; 
          font-weight: bold; 
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .answer-letter { 
          font-weight: bold; 
          margin: 0; 
          padding: 0;
          display: inline;
        }
        .answer-text { 
          margin: 0 0 0.5em 0; 
          padding: 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }
        table { 
          page-break-inside: auto;
          width: 100%;
          table-layout: fixed;
        }
        tr { 
          page-break-inside: avoid;
          page-break-after: auto;
        }
        td {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      </style>
      ${content}
    `;
    element.style.padding = '20px';
    element.className = 'pdf-container';

    // Prefer the first passage title for filename
    const safeTitle = sanitizeFilename(
      assessmentResponse.passages?.[0]?.title || 'passage',
      'passage',
    );

    try {
      await exportElementToPdf(element, {
        filename: `${safeTitle}.pdf`,
        margin: 0.7,
        pagebreakBefore: ['.next-passage', '.questions-section', '.answer-key-section'],
        pagebreakAvoid: [
          '.passage-section',
          '.question-block',
          '.answer-choices',
          '.answer-key-item',
          '.question-wrapper',
          '.answer-wrapper',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li',
        ],
      });
      handleDownloadClose();
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    }
  };

  // -- Download as Word --
  const handleDownloadWord = () => {
    if (!assessmentResponse) return;

    const content = createPrintContent(
      assessmentResponse.passages,
      assessmentResponse.combinedQuestions,
      assessmentResponse.answerKey,
      assessmentResponse.isPairedPassage,
      'word',
      standards.filter(s => selectedStandards.includes(s._id)).map(s => s.code || s.standard).filter(Boolean) as string[]
    );

    const formattedContent = `
      <html xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns:v="urn:schemas-microsoft-com:vml"
            xmlns:o="urn:schemas-microsoft-com:office:office">
        <head>
          <meta charset="utf-8">
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
              <w:BrowserLevel>MicrosoftInternetExplorer4</w:BrowserLevel>
            </w:WordDocument>
          </xml>
          <style>
            /* Reset styles */
            * {
              margin: 0;
              padding: 0;
              border: 0;
              font-size: 100%;
              vertical-align: baseline;
            }

            /* Document settings */
            @page Section1 {
              size: 8.5in 11.0in;
              margin: 1.0in;
              mso-header-margin: .5in;
              mso-footer-margin: .5in;
              mso-paper-source: 0;
            }

            div.WordSection1 {
              page: Section1;
            }

            /* Basic styles */
            body {
              font-family: 'Times New Roman', Times, serif;
              font-size: 12pt;
              line-height: 1.15;
            }

            /* Passage styles */
            .passage-section {
              margin-bottom: 20pt;
            }

            .passage-title {
              text-align: center;
              font-size: 16pt;
              font-weight: bold;
              margin-bottom: 12pt;
              color: #2c5282;
              border-bottom: 1pt solid #2c5282;
              padding-bottom: 6pt;
            }

            .passage-content {
              margin-bottom: 12pt;
            }

            .passage-content p {
              margin-bottom: 12pt;
              text-indent: 36pt;
              text-align: justify;
            }

            /* Questions section */
            .questions-section {
              margin-top: 24pt;
            }

            .questions-section h2 {
              font-size: 14pt;
              margin-bottom: 18pt;
            }

            /* Question styles */
            .question-text {
              margin-bottom: 12pt;
            }

            .answer-letter {
              padding-right: 6pt;
            }

            /* Answer key section */
            .answer-key-section {
              margin-top: 24pt;
              page-break-before: always;
              page-break-inside: avoid;
            }

            .answer-key-section h2 {
              font-size: 14pt;
              margin-bottom: 18pt;
              text-align: center;
            }

            .answer-key-section table {
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid;
            }

            .answer-key-section td {
              padding: 3pt 6pt;
              vertical-align: top;
            }

            .answer-key-section h3 {
              font-size: 12pt;
              margin: 0;
              font-weight: bold;
            }

            .standard-reference {
              color: #666;
              margin: 0;
            }

            .explanation {
              margin: 0;
            }

            .answer {
              margin: 0;
              font-weight: bold;
            }

            /* MSO specific table styles */
            table {
              mso-table-lspace: 0pt;
              mso-table-rspace: 0pt;
            }

            tr {
              mso-row-margin: 0pt;
            }

            td {
              mso-cell-padding: 0pt;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;

    const safeTitle = sanitizeFilename(
      assessmentResponse.passages?.[0]?.title || 'passage',
      'passage',
    );
    downloadWordHtmlBlob(formattedContent, `${safeTitle}.doc`);
    handleDownloadClose();
  };

  return {
    showSuccessAlert,
    successMessage,
    handleCloseSuccess,
    error,
    setError,
    isFormCollapsed,
    setIsFormCollapsed,
    handleSubmit,
    gradeLevel,
    handleGradeLevelChange,
    loadingGradeLevels,
    availableGradeLevels,
    selectedStrand,
    setSelectedStrand,
    setSelectedStandards,
    availableStrands,
    strandFilteredStandards,
    selectedStandards,
    standardLoading,
    capitalizeFirstLetter,
    topic,
    setTopic,
    language,
    setLanguage,
    isPairedPassage,
    setIsPairedPassage,
    paragraphCount,
    setParagraphCount,
    getDefaultParagraphCount,
    genre,
    setGenre,
    genreTwo,
    setGenreTwo,
    revisingAndEditing,
    setRevisingAndEditing,
    generateQuestions,
    setGenerateQuestions,
    questionStyle,
    setQuestionStyle,
    questionMix,
    setQuestionMix,
    isStreaming,
    isLoading,
    generateImages,
    setGenerateImages,
    imageCount,
    setImageCount,
    currentStatus,
    streamingPassages,
    streamingQuestionsContent,
    streamingAnswerKeyContent,
    isChatProcessing,
    isQuestionsComplete,
    assessmentResponse,
    generationStreamingQuestions,
    generationStreamingCombinedQuestions,
    chatHistory,
    chatMessage,
    setChatMessage,
    handleChatSubmit,
    handleChatCancel,
    anchorEl,
    handleDownloadClose,
    handleDownloadPDF,
    handleDownloadWord,
    hasUnsavedChanges,
    handleManualSave,
    isSaving,
    handlePrint,
    handleDownloadClick,
  };
}
